import * as React from 'react';
import { ReactElement, ReactNode, useContext, useEffect } from 'react';
import {
  AdminDataOrganisationDTO,
  ExternalIdDTO,
  OrganisationDTO,
  OrganisationInstitutionalIdDTO,
  OrganisationOtherNameDTO,
  OrganisationToPersonDTO,
  ReferencedOrganisationDTO,
  ReferencedProjectDTO,
  SubjectDTO,
  UrlDTO,
} from '../../../api/core';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import { Layout } from '../../Layout';
import { IntlShape, useIntl } from 'react-intl';
import { FormContent } from '../FormContent';
import { Validation } from '../../../util/validation/interfaces';
import { validate } from '../../../util/validation/validate';
import { CheckPermissionFunction, PermissionGate } from '../../../Auth/components/PermissionGate';
import { Urls } from '../components/parts/Urls';
import { ExternalIds } from '../components/parts/ExternalIds';
import { InstitutionalIds } from '../components/parts/InstitutionalIds';
import { Subjects } from '../components/parts/Subjects';
import { OrganisationPersons } from '../components/parts/OrganisationPersons';
import { OtherNames } from '../components/parts/OtherNames';
import { ReferencedOrganisations } from '../components/parts/ReferencedOrganisations';
import { ReferencedProjects } from '../components/parts/ReferencedProjects';
import { EditorialStatus } from '../components/parts/EditorialStatus';
import { Block } from '../components/layout/Block';
import { LabelledInputText } from '../../components/form/LabelledInputText';
import { LabelledInputNumber } from '../../components/form/LabelledInputNumber';
import { LabelledInputTextarea } from '../../components/form/LabelledInputTextarea';
import { getVocabularyOptions } from '../../../Vocabulary/util';
import { Hidden } from '../components/parts/Hidden';
import { LabelledCalendar } from '../../components/form/LabelledCalendar';

export interface EditProps {
  modifiedModel: OrganisationDTO;
  submit: () => void;
  updateField: (field: keyof OrganisationDTO, value: any) => void;
  updateAdminField: (field: keyof AdminDataOrganisationDTO, value: any) => void;
  saving: boolean;
}

export function FormOrganisation(props: EditProps): ReactNode {
  const intl: IntlShape = useIntl();
  const {session} = useContext(AuthContext);

  const validation: Validation = validate(props.modifiedModel, props.modifiedModel.adminDataOrganisation, 'organisation', session);

  const [initiallyLoaded, setInitiallyLoaded] = React.useState(false);
  useEffect(() => {
    setInitiallyLoaded(true);
  }, []);

  return (
    <Layout
      title={
        props.modifiedModel.id
          ? intl.formatMessage({ id: 'organisation.header.edit' })
          : intl.formatMessage({ id: 'organisation.header.create' })
      }
      headerImage={require('../../../Settings/img/banners/edit/organisation.svg')}
    >
      <FormContent onSave={props.submit} validation={validation} isNew={!props.modifiedModel.id} saving={props.saving}>
        <Block title={'organisation.header.basic'}>
          <PermissionGate always={false} entity="organisation" field="name">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledInputText
                  id={`name`}
                  autoFocus={true}
                  value={props.modifiedModel.name}
                  permissions={checkPermission()}
                  validation={validation?.root?.fields?.name}
                  onChange={(value): void => props.updateField('name', value)}
                  title={'organisation.attr.name'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate entity="organisation" always={false} field="other_names">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-12'}>
                <OtherNames
                  autoFocus={initiallyLoaded}
                  otherNames={props.modifiedModel.otherNames || []}
                  onChange={(otherNames: OrganisationOtherNameDTO[]): void =>
                    props.updateField('otherNames', otherNames)
                  }
                  checkPermission={checkPermission}
                  validation={validation?.root?.fields?.other_names}
                  label={'organisation.attr.otherNames'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation" field="urls">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-12'}>
                <Urls
                  autoFocus={initiallyLoaded}
                  title={'organisation.attr.urls'}
                  checkPermission={checkPermission}
                  urls={props.modifiedModel.urls || []}
                  typelist={'url_list'}
                  onChange={(u: UrlDTO[]): void => props.updateField('urls', u)}
                  validation={validation?.root?.fields?.urls}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation" field="start_date">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-6'}>
                <LabelledCalendar
                  id={'start_date'}
                  value={props.modifiedModel.startDate}
                  permissions={checkPermission()}
                  validation={validation?.root?.fields?.start_date}
                  disabled={!checkPermission().canWrite}
                  onChange={(startDate: Date): void => props.updateField('startDate', startDate)}
                  title={'organisation.attr.startDate'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation" field="end_date">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-6'}>
                <LabelledCalendar
                  id={'end_date'}
                  value={props.modifiedModel.endDate}
                  permissions={checkPermission()}
                  validation={validation?.root?.fields?.end_date}
                  onChange={(endDate): void => props.updateField('endDate', endDate)}
                  title={'organisation.attr.endDate'}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
        <Block title={'organisation.header.ids'}>
          <PermissionGate always={false} entity="organisation" field="external_ids">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ExternalIds
                  autoFocus={initiallyLoaded}
                  title={'organisation.attr.externalIds'}
                  label={'organisation.attr.externalId'}
                  extIds={props.modifiedModel.externalIds || []}
                  onChange={(externalIds: ExternalIdDTO[]): void => props.updateField('externalIds', externalIds)}
                  options={getVocabularyOptions(intl.locale, 'external_unit_id_list')}
                  validation={validation?.root?.fields?.external_ids}
                  checkPermission={checkPermission}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation" field="organisation_institutional_ids">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <InstitutionalIds
                  autoFocus={initiallyLoaded}
                  title={'organisation.attr.institutionalIds'}
                  checkPermission={checkPermission}
                  typesList={'affiliations_list'}
                  institutionalIds={props.modifiedModel.organisationInstitutionalIds || []}
                  onChange={(iId: OrganisationInstitutionalIdDTO[]): void =>
                    props.updateField('organisationInstitutionalIds', iId)
                  }
                  validation={validation?.root?.fields?.organisation_institutional_ids}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
        <Block title={'organisation.header.administration'}>
          <PermissionGate always={false} entity="organisation" field="id">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledInputNumber
                  id={'id'}
                  value={props.modifiedModel.id || null}
                  permissions={checkPermission()}
                  validation={validation?.root?.fields?.id}
                  min={0}
                  allowEmpty={true}
                  maxFractionDigits={0}
                  useGrouping={false}
                  title={'organisation.attr.id'}
                  onChange={(): void => console.warn('ID cannot be edited')}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation_admin_data" field="hidden">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12 flex'}>
                <Hidden
                  label={'organisation.attr.hidden'}
                  checked={props.modifiedModel.adminDataOrganisation.hidden || false}
                  disabled={!checkPermission().canWrite}
                  invalid={!validation?.root?.fields?.id.valid}
                  required={checkPermission().required}
                  onChange={(hidden: boolean): void => props.updateAdminField('hidden', hidden)}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation" field="note">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledInputTextarea
                  id={'note'}
                  value={props.modifiedModel.note}
                  permissions={checkPermission()}
                  validation={validation?.root?.fields?.note}
                  onChange={(value): void => props.updateField('note', value)}
                  autoResize={true}
                  rows={3}
                  title={'organisation.attr.note'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation_admin_data" field="editorial_status">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <EditorialStatus
                  label={'organisation.attr.editorialStatus'}
                  editorialStatus={props.modifiedModel.adminDataOrganisation.editorialStatus}
                  onChange={(value): void => props.updateAdminField('editorialStatus', value)}
                  checkPermission={checkPermission}
                  validation={validation?.admin?.fields?.editorial_status}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
        <Block title={'organisation.header.linking'}>
          <PermissionGate always={false} entity="organisation" field="organisation_subjects">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <Subjects
                  autoFocus={initiallyLoaded}
                  title={'organisation.attr.subjects'}
                  checkPermission={checkPermission}
                  onChange={(subjects: SubjectDTO[]): void => props.updateField('organisationSubjects', subjects)}
                  subjects={props.modifiedModel.organisationSubjects || []}
                  validation={validation?.root?.fields?.organisation_subjects}
                  type={'organisation'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation" field="projects">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ReferencedProjects
                  autoFocus={initiallyLoaded}
                  label={'organisation.attr.projects'}
                  projects={props.modifiedModel.projects || []}
                  validation={validation?.root?.fields?.projects}
                  checkPermission={checkPermission}
                  multiple={true}
                  onChange={(projects: ReferencedProjectDTO[]): void => props.updateField('projects', projects)}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate entity="organisation" always={false} field="organisation_persons">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-12'}>
                <OrganisationPersons
                  autoFocus={initiallyLoaded}
                  title={'organisation.attr.organisationPersons'}
                  organisationPersons={props.modifiedModel.organisationPersons || []}
                  onChange={(organisationPersons: OrganisationToPersonDTO[]): void =>
                    props.updateField('organisationPersons', organisationPersons)
                  }
                  checkPermission={checkPermission}
                  validation={validation?.root?.fields?.organisation_persons}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation" field="childs">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ReferencedOrganisations
                  autoFocus={initiallyLoaded}
                  label={'organisation.attr.childs'}
                  organisations={props.modifiedModel.childs || []}
                  validation={validation?.root?.fields?.childs}
                  checkPermission={checkPermission}
                  onChange={(partner: ReferencedOrganisationDTO[]): void => props.updateField('childs', partner)}
                  multiple={true}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation" field="parents">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ReferencedOrganisations
                  autoFocus={initiallyLoaded}
                  label={'organisation.attr.parents'}
                  organisations={props.modifiedModel.parents || []}
                  validation={validation?.root?.fields?.parents}
                  checkPermission={checkPermission}
                  onChange={(partner: ReferencedOrganisationDTO[]): void => props.updateField('parents', partner)}
                  multiple={false}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation" field="predecessors">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ReferencedOrganisations
                  autoFocus={initiallyLoaded}
                  label={'organisation.attr.predecessors'}
                  organisations={props.modifiedModel.predecessors || []}
                  validation={validation?.root?.fields?.predecessors}
                  checkPermission={checkPermission}
                  onChange={(partner: ReferencedOrganisationDTO[]): void => props.updateField('predecessors', partner)}
                  multiple={true}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="organisation" field="successor">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ReferencedOrganisations
                  autoFocus={initiallyLoaded}
                  label={'organisation.attr.successors'}
                  organisations={props.modifiedModel.successor || []}
                  validation={validation?.root?.fields?.successor}
                  checkPermission={checkPermission}
                  onChange={(partner: ReferencedOrganisationDTO[]): void => props.updateField('successor', partner)}
                  multiple={true}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
      </FormContent>
    </Layout>
  );
}

export default FormOrganisation;
